import { useTeamStats, type DailyTeamStats } from "./useTeamStats";
import { useWorkerProductivity, type WorkerStats } from "./useWorkerProductivity";
import {
  type HealthStatus,
  safetyHealth,
  qualityHealth,
  deliveryHealth,
  inventoryHealth,
  productivityHealth,
} from "./healthThresholds";

export interface SQDIPSummary {
  safety: { headline: number; health: HealthStatus };
  quality: { headline: number; health: HealthStatus };
  delivery: { headline: number; health: HealthStatus };
  inventory: { headline: number; health: HealthStatus };
  productivity: { headline: number; health: HealthStatus };
}

export function computeSummary(
  daily: DailyTeamStats[],
  queueTotal: number,
  workers: WorkerStats[],
): SQDIPSummary {
  const totalBreaches = daily.reduce((s, d) => s + d.slaBreaches, 0);
  const totalCompleted = daily.reduce((s, d) => s + d.itemsCompleted, 0);
  const totalCreated = daily.reduce((s, d) => s + d.itemsCreated, 0);
  const totalRework = daily.reduce((s, d) => s + d.reworkCount, 0);
  const completionRate = totalCreated > 0 ? (totalCompleted / totalCreated) * 100 : 100;
  const reworkRate = totalCompleted > 0 ? (totalRework / totalCompleted) * 100 : 0;
  const avgUtilization = workers.length > 0
    ? workers.reduce((s, w) => s + w.utilization, 0) / workers.length
    : 0;

  return {
    safety: { headline: totalBreaches, health: safetyHealth(totalBreaches) },
    quality: { headline: Math.round(reworkRate * 10) / 10, health: qualityHealth(reworkRate) },
    delivery: { headline: Math.round(completionRate), health: deliveryHealth(completionRate) },
    inventory: { headline: queueTotal, health: inventoryHealth(queueTotal) },
    productivity: { headline: Math.round(avgUtilization), health: productivityHealth(avgUtilization) },
  };
}

export function useSQDIPDashboard(teamId: string, dateRange: string) {
  const teamStats = useTeamStats(teamId, dateRange);
  const workerStats = useWorkerProductivity(teamId, dateRange);

  const loading = teamStats.isLoading || workerStats.isLoading;
  const error = teamStats.error?.message ?? workerStats.error?.message ?? null;

  const summary = teamStats.data && workerStats.data
    ? computeSummary(teamStats.data.daily, teamStats.data.queueStats.total, workerStats.data)
    : null;

  return {
    summary,
    daily: teamStats.data?.daily ?? [],
    queueStats: teamStats.data?.queueStats ?? null,
    workers: workerStats.data ?? [],
    loading,
    error,
    refetch: () => {
      teamStats.refetch();
      workerStats.refetch();
    },
  };
}
